import React, { useState, useEffect } from 'react';
import { UuidGeneratorService, UuidVersion, UuidFormat } from '../../lib/services/uuidGeneratorService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { EncoderToolType } from '../../lib/types';
import './UuidGenerator.css';

export const UuidGenerator: React.FC = () => {
  const [output, setOutput] = useState('');
  const [version, setVersion] = useState<UuidVersion>('v4');
  const [format, setFormat] = useState<UuidFormat>('standard');
  const [quantity, setQuantity] = useState(1);
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await UuidGeneratorService.generate(version, quantity, format);
      setOutput(result.outputData as string);
      setError(null);
    } catch (err) {
      const errorMessage = ErrorHandler.handle(err, EncoderToolType.UUID_GENERATOR);
      setError(errorMessage);
      setOutput('');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleGenerate();
  }, [version, format, quantity]);

  const handleCopy = async () => {
    if (!output) return;

    try {
      const success = await ClipboardUtils.copyText(output);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy UUID';
    }
  };

  const getButtonClassName = () => {
    const baseClass = 'copy-button';
    if (!output) return `${baseClass} copy-idle`;
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  return (
    <div className="uuid-generator">
      <div style={{ marginBottom: '2rem' }}>
        <h1>UUID Generator</h1>
        <p>
          Generate Universally Unique Identifiers (UUIDs) in different versions and formats.
          Supports UUID v1 (timestamp-based) and v4 (random).
        </p>
      </div>

      <div className="encoder-container">
        <div className="form-group">
          <label>
            Version:
          </label>
          <select
            value={version}
            onChange={(e) => setVersion(e.target.value as UuidVersion)}
            data-testid="version-select"
          >
            <option value="v4">Version 4 (Random)</option>
            <option value="v1">Version 1 (Timestamp)</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            Format:
          </label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as UuidFormat)}
            data-testid="format-select"
          >
            <option value="standard">Standard (lowercase)</option>
            <option value="uppercase">Uppercase</option>
            <option value="no-hyphens">No Hyphens</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            Quantity:
          </label>
          <input
            type="number"
            min="1"
            max="20"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
            data-testid="quantity-input"
          />
        </div>

        {error && (
          <div className="status-message status-error">
            {error}
          </div>
        )}

        <div className="form-group">
          <label>
            Generated UUID{quantity > 1 ? 's' : ''}:
          </label>
          <textarea
            value={output}
            readOnly
            placeholder={isLoading ? 'Generating...' : 'UUID will appear here...'}
            className={isLoading ? 'loading' : ''}
            data-testid="output-text"
          />
          {isLoading && (
            <div className="loading-indicator">Generating...</div>
          )}
        </div>

        <div className="button-container">
          <button
            onClick={handleCopy}
            disabled={!output}
            className={getButtonClassName()}
            data-testid="copy-button"
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};
